import { Vector } from 'mdatools/arrays';
import { mean, variance } from 'mdatools/stat';
import { pnorm } from 'mdatools/distributions';

import { pcares_get } from '../../shared/wasm/dist/pca.js';
import { convertToIntervals } from '../../shared/utils.js';


/**
 * Compute SIMCA predictions for a particular component for alternative dataset
 */
export function simca_predict_alt(pcares, simca) {

   // get matrices with h and q distances
   const nrows = pcares.nrows;
   const ncomp = pcares.ncomp;
   const ncols = pcares.ncols;

   // we will make classification assuming that all objects are from alternative class
   const refClasses = Array.from({length: nrows}, () => ("alternative"));

   // variables to collect statistics
   const specificity = new Array(ncomp);
   const selectivity = new Array(ncomp);
   const components = new Array(ncomp);

   // get object with results for each component
   const res = new Array(ncomp);
   for (let a = 0; a < ncomp; a++) {

      // get usual results
      const ra = simca_predict_single(simca, pcares, a, false);

      let I = nrows;

      // get distances
      const f = ra.f;
      const fcrit = simca.eCrit[a];

      // get DoF for full distance
      const k = simca.Nf[a];

      // 	Order f' such that f1'≤ f2'≤.... ≤ fI''
      const fv = new Vector(f);
      let fp = fv.sort();
      let ind = fv.sortind();

      // prepare array for roles
      const roles = Array.from({length: nrows}, () => ("alien"));;

      // Step 2. If Disc <0
      let Disc = -1;
      let n = 0;
      let m = 0;
      let d = 0;
      let M1 = 0;

      while (Disc < 0) {
         if (n > 0) {
            roles[ind.v[I - 1] - 1] = 'external';
            I = I - 1;
            ind = ind.slice(1, I);
            fp = fp.slice(1, I);
         }
         m = mean(fp);
         d = variance(fp, true);
         M1 = d / (m * m);
         Disc = 4 - 2 * k * M1;
         n = n + 1;
      }

      // Step 3. Calculate  x  by Eq. (18)
      const x = (2 + Math.sqrt(Disc)) / M1;

      // 	Calculate  s and  f'0 by Eq. (19)
      const s = (x - k);
      const f0 = m / x;

      // 	Calculate: z, h, r, p, Mz, Sz by Eq. (20)
      const z = fcrit / f0;
      const h = 1 - 2 * (k + s) * (k + 3 * s) / (3 * (k + 2 * s)**2);
      const r = (h - 1) * (1 - 3 * h);
      const p = (k + 2 * s) / (k + s)**2;
      const Mz = 1 + h * p * (h - 1 - 0.5 * (2 - h) * r * p);
      const Sz = h * Math.sqrt(2 * p) * (1 + 0.5 * r * p);

      // Step 3.	If α is given then calculate β by Eq. (21)
      const beta = pnorm((Math.pow(z / (k + s), h) - Mz) / Sz);

      res[a] = ra;
      res[a].beta = beta;
      res[a].s = s;
      res[a].k = k;
      res[a].Mz = Mz;
      res[a].Sz = Sz;
      res[a].m = m;
      res[a].f0 = f0;
      res[a].hz = h;
      res[a].anum = {'aliens': I, 'external': nrows - I};
      res[a].a = roles;


      components[a] = a + 1;
      selectivity[a] = 1 - beta;
      specificity[a] = ra.TN / (ra.TN + ra.FP);
   }

   return {
      class: ['simcares'],
      v: res,
      className: simca.className,
      alpha: simca.alpha,
      limType: simca.limType,
      ncomp: simca.ncomp,
      nrows: nrows,
      ncols: ncols,
      classes: pcares.classes,
      classNames: pcares.classes && pcares.classes.length > 0 ? [...new Set(pcares.classes)] : null,
      objlabels: pcares.objlabels,
      stat: {specificity, selectivity, components}
   }
}

/**
 * Compute SIMCA predictions for a particular component
 */
export function simca_predict_single(simca, pcares, a, target) {

   // get object distances
   const f = pcares_get(pcares, (simca.limType === 'robust' ? 'fdistr' : 'fdistc'), a + 1);
   const h = pcares_get(pcares, 'hdist', a + 1);
   const q = pcares_get(pcares, 'qdist', a + 1);

   // get variable distances
   const i = pcares_get(pcares, (simca.limType === 'robust' ? 'idistr' : 'idistc'), a + 1);
   const g = pcares_get(pcares, 'gdist', a + 1);
   const r = pcares_get(pcares, 'rdist', a + 1);

   const nrows = f.length;

   // get object distance parameters
   const h0 = simca.h0[a];
   const Nh = simca.Nh[a];
   const q0 = simca.q0[a];
   const Nq = simca.Nq[a];
   const Nf = simca.Nf[a];

   // get variable distance parameters
   const g0 = simca.g0[a];
   const Ng = simca.Ng[a];
   const r0 = simca.r0[a];
   const Nr = simca.Nr[a];
   const Ni = simca.Ni[a];

   // get critical limits (for objects and variables)
   const oCrit = simca.oCrit[a];
   const eCrit = simca.eCrit[a];
   const voCrit = simca.voCrit[a];
   const veCrit = simca.veCrit[a];

   // prepare array for object categories
   const c = new Array(nrows);
   const d = new Array(nrows);
   const cnum = {'regular': 0, 'extreme': 0, 'outliers': 0};
   const dnum = {'in': 0, 'out': 0};

   let TP = 0, FP = 0, TN = 0, FN = 0;
   for (let k = 0; k < nrows; k++) {
      const fk = f[k];
      if (fk < eCrit) {
         c[k] = 'regular';
         d[k] = 'in';
         cnum.regular += 1;
         dnum.in += 1;
      } else if (fk > oCrit) {
         c[k] = 'outlier';
         d[k] = 'out';
         cnum.outliers += 1;
         dnum.out += 1;
      } else {
         c[k] = 'extreme';
         d[k] = 'out';
         cnum.extreme += 1;
         dnum.out += 1;
      }

      TP = TP + (d[k] === 'in' && target);
      FN = FN + (d[k] !== 'in' && target);
      TN = TN + (d[k] !== 'in' && !target);
      FP = FP + (d[k] === 'in' && !target);
   }


   return {
      h, h0, Nh, q, q0, Nq, f, Nf, eCrit, oCrit, c, d, cnum, dnum, TP, TN, FN, FP,
      g, g0, Ng, r, r0, Nr, i, Ni, veCrit, voCrit
   };
}

/**
 * Compute SIMCA predictions.
 *
 * @param {Object} pcares - JSON with predicted PCA results.
 * @param {Object} simca - JSON with SIMCA parameters.
 * @param {outliers} classes - array with outliers.
 *
 * @returns {Object} JSON with SIMCA parameters.
 */
export function simca_predict(pcares, simca, outliers) {

   // get matrices with h and q distances
   const nrows = pcares.nrows;
   const ncomp = pcares.ncomp;
   const ncols = pcares.ncols;


   // get object with results for each component
   const res = new Array(ncomp);
   const sensitivity = new Array(ncomp);
   const components = new Array(ncomp)
   for (let a = 0; a < ncomp; a++) {
      const ra = simca_predict_single(simca, pcares, a, true);
      sensitivity[a] = ra.TP / (ra.TP + ra.FN);
      components[a] = a + 1;
      res[a] = ra;
   }

   return {
      class: ['simcares'],
      target: true,
      v: res,
      className: simca.className,
      alpha: simca.alpha,
      limType: simca.limType,
      ncomp: simca.ncomp,
      nrows: nrows,
      ncols: ncols,
      classes: pcares.classes,
      objlabels: pcares.objlabels,
      objindices: pcares.objindices,
      varlabels: pcares.varlabels,
      varindices: pcares.varindices,
      varvalues: pcares.varvalues,
      stat: {sensitivity, components}
   }
}


// function for downloading of PV-set
export function downloadResults(res, dataset, name, outliers, ncomp, exclvars) {

   if (!outliers || !Array.isArray(outliers)) {
      outliers = [];
   }

   if (!exclvars || !Array.isArray(exclvars)) {
      exclvars = [];
   }

   const nAll = dataset.values.nrows;
   const nOut = outliers.length;
   const n = nAll - nOut;
   const v = res.v[ncomp - 1];

   const hasClasses = res.classes && res.classes.length === n;
   const filler = hasClasses ? ['', '', '', '', ''] : ['', '', '', ''];
   let out = '';
   out += ['Reference classes:',  hasClasses ? 'provided' : 'not provided', ...filler].join(dataset.sep) + '\n';
   out += ['Number of components:', ncomp, ...filler].join(dataset.sep) + '\n';
   out += ['Alpha:', (res.alpha/100).toFixed(3), ...filler].join(dataset.sep) + '\n';
   out += ['Limits estimator:', res.limType, ...filler].join(dataset.sep) + '\n';
   out += '\n';

   out += ['Nh:', v.Nh, ...filler].join(dataset.sep) + '\n';
   out += ['Nq:', v.Nq, ...filler].join(dataset.sep) + '\n';
   out += ['Nf:', v.Nf, ...filler].join(dataset.sep) + '\n';
   out += '\n';

   out += ['F crit (extremes):', v.eCrit, ...filler].join(dataset.sep) + '\n';
   out += ['F crit (outliers):', v.oCrit, ...filler].join(dataset.sep) + '\n';
   out += '\n';

   if (exclvars.length > 0) {
      out += ['Number of excluded varibles:', exclvars.length, ...filler].join(dataset.sep) + '\n';
      out += ['Indices of excluded varibles:', convertToIntervals(exclvars), ...filler].join(dataset.sep) + '\n';
      out += '\n';
   }

   if (v.beta) {
      // there can be no removed outliers in this case
      out += ['Objects treated as from non-target class', '', ...filler].join(dataset.sep) + '\n';
      out += ['Objects:', nAll, ...filler].join(dataset.sep) + '\n';
      out += ['In:', v.dnum.in, ...filler].join(dataset.sep) + '\n';
      out += ['Out:', v.dnum.out, ...filler].join(dataset.sep) + '\n';
      out += ['Aliens:', v.anum.aliens, ...filler].join(dataset.sep) + '\n';
      out += ['External:', v.anum.external, ...filler].join(dataset.sep) + '\n';
      out += ['Specificity:', res.stat.specificity ? res.stat.specificity[ncomp - 1] : '-', ...filler].join(dataset.sep) + '\n';
      out += ['Selectivity:', res.stat.selectivity ? res.stat.selectivity[ncomp - 1] : '-', ...filler].join(dataset.sep) + '\n';
      out += ['Beta:', v.beta, ...filler].join(dataset.sep) + '\n';
      out += '\n';

      out += ['', 'object', 'class', 'role', 'decision', 'h/h0', 'q/q0', 'f'].join(dataset.sep) + '\n';

      for (let i = 0; i < nAll; i++) {
         out += [
            '',
            dataset.objlabels[i],
            res.classes[i],
            v.a[i],
            v.d[i],
            v.h[i] / v.h0,
            v.q[i] / v.q0,
            v.f[i]
         ].join(dataset.sep) + '\n';
      }

   } else {
      // there can be no removed outliers in this case
      out += ['Objects treated as from target class', '', ...filler].join(dataset.sep) + '\n';
      out += ['Objects:', nAll, ...filler].join(dataset.sep) + '\n';
      out += ['Removed:', nOut, ...filler].join(dataset.sep) + '\n';
      out += ['In:', v.dnum.in, ...filler].join(dataset.sep) + '\n';
      out += ['Out:', v.dnum.out, ...filler].join(dataset.sep) + '\n';
      out += ['Regular:', v.cnum.regular, ...filler].join(dataset.sep) + '\n';
      out += ['Extreme:', v.cnum.extreme, ...filler].join(dataset.sep) + '\n';
      out += ['Outliers:', v.cnum.outliers, ...filler].join(dataset.sep) + '\n';
      out += ['Sensitivity:', res.stat.sensitivity ? res.stat.sensitivity[ncomp - 1] : '-', ...filler].join(dataset.sep) + '\n';
      out += '\n';

      out += ['', 'object', 'class', 'role', 'decision', 'h/h0', 'q/q0', 'f'].join(dataset.sep) + '\n';

      let j = 0;
      for (let i = 0; i < nAll; i++) {

         if (nOut > 0 && outliers.includes(i)) {
            out += [
               '',
               dataset.objlabels[i],
               '-',
               'removed',
               '-',
               '-',
               '-',
               '-'
            ].join(dataset.sep) + '\n';
            continue;
         }

         out += [
            '',
            dataset.objlabels[j],
            res.classes[j],
            v.c[j],
            v.d[j],
            v.h[j] / v.h0,
            v.q[j] / v.q0,
            v.f[j]
         ].join(dataset.sep) + '\n';
         j = j + 1;
      }
   }
   out += '\n';



   if (dataset.dec !== '.') {
      out = out.replace(/\./g, dataset.dec);
   }

   out = ['Target class:', res.className, ...filler].join(dataset.sep) + '\n'  + out;
   out = ['Data filename:', dataset.fileName, ...filler].join(dataset.sep) + '\n' + out;

   let hdr = ['DD-SIMCA results (' + name + ')', 'https://mda.tools/ddsimca/', ...filler].join(dataset.sep) + '\n\n';

   const blob = new Blob([hdr + out], { type: 'text/csv' });
   const url = window.URL.createObjectURL(blob)
   const a = document.createElement('a')
   a.setAttribute('href', url)
   a.setAttribute('download', `ddsimca-results-${name}.csv`);
   a.click()
}


// function for downloading of PV-set
export function downloadModel(pcamodel, simca, ncomp) {

   const blob = new Blob([JSON.stringify({
      pca: {
         hash: pcamodel.hash,
         class: pcamodel.class,
         ncomp: pcamodel.ncomp,
         nrows: pcamodel.nrows,
         ncols: pcamodel.ncols,
         center: pcamodel.center,
         scale: pcamodel.scale,
         lim: {
            classic: {
               Nh: Array.from(pcamodel.lim.classic.Nh),
               Nq: Array.from(pcamodel.lim.classic.Nq),
               h0: Array.from(pcamodel.lim.classic.h0),
               q0: Array.from(pcamodel.lim.classic.q0),
               Ng: Array.from(pcamodel.lim.classic.Ng),
               Nr: Array.from(pcamodel.lim.classic.Nr),
               g0: Array.from(pcamodel.lim.classic.g0),
               r0: Array.from(pcamodel.lim.classic.r0)
            },
            robust: {
               Nh: Array.from(pcamodel.lim.robust.Nh),
               Nq: Array.from(pcamodel.lim.robust.Nq),
               h0: Array.from(pcamodel.lim.robust.h0),
               q0: Array.from(pcamodel.lim.robust.q0),
               Ng: Array.from(pcamodel.lim.robust.Ng),
               Nr: Array.from(pcamodel.lim.robust.Nr),
               g0: Array.from(pcamodel.lim.robust.g0),
               r0: Array.from(pcamodel.lim.robust.r0)
            }
         },
         exclvars: pcamodel.exclvars,              // array with indices of excluded variables - reserved for future use
         varindices: pcamodel.varindices,          // array wuth variable indices without excluded
         varlabels: pcamodel.varlabels,            // array with variable labels without excluded
         varvalues: pcamodel.varvalues,            // array with variable values (wavelength, etc.) without excluded
         varvaluesName: pcamodel.varvaluesName,    // name of the variable values (if any)
         varvaluesUnits: pcamodel.varvaluesUnits,  // units of the variable values (if any)
         varrev: pcamodel.varrev,                  // logical, if variables must be reversed

         prep: [],                           // array with preprocessing methods — reserved for future use
         v: Array.from(pcamodel.v)           // array with all model outcomes (loadings, variances, etc)
      },
      simca: {
         ncomp: ncomp,
         nrows: simca.nrows,
         ncols: simca.ncols,
         alpha: simca.alpha,
         gamma: simca.gamma,
         limType: simca.limType,
         h0: Array.from(simca.h0),
         q0: Array.from(simca.q0),
         g0: Array.from(simca.g0),
         r0: Array.from(simca.r0),
         Nh: Array.from(simca.Nh),
         Nq: Array.from(simca.Nq),
         Nf: Array.from(simca.Nf),
         Ng: Array.from(simca.Ng),
         Nr: Array.from(simca.Nr),
         Ni: Array.from(simca.Ni),
         eCrit: Array.from(simca.eCrit),
         oCrit: Array.from(simca.oCrit),
         veCrit: Array.from(simca.veCrit),
         voCrit: Array.from(simca.voCrit),
         className: simca.className,
         hash: simca.hash,
      }
   })], { type: 'application/json' });

   const url = window.URL.createObjectURL(blob)
   const a = document.createElement('a')
   a.setAttribute('href', url)
   a.setAttribute('download', 'ddsimca-model.json');
   a.click()
}
